(() => {
  'use strict';
  const init = (root) => {
    if (root.dataset.hh26Ready) return;
    root.dataset.hh26Ready = 'true';
    const controller = new AbortController();
    const { signal } = controller;
    const mobile = window.matchMedia('(max-width: 749px)');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const video = root.querySelector('[data-hh26-video]');
    const motion = root.querySelector('[data-hh26-motion]');
    const sticky = root.querySelector('[data-hh26-sticky]');
    let wantsPlayback = false;
    let inView = true;
    let raf = 0;
    let loaded = false;
    let mediaObserver;

    const setVideoState = () => {
      if (!video || !motion) return;
      const playing = !video.paused && !video.ended;
      motion.classList.toggle('is-playing', playing || video.currentTime > 0);
    };
    const play = async () => {
      if (!video || !wantsPlayback || !inView || document.hidden) return;
      if (!loaded) {
        const source = video.querySelector('source[data-src]');
        source.src = source.dataset.src;
        video.load();
        loaded = true;
      }
      try { await video.play(); } catch (_) { /* The useful poster remains visible if autoplay is denied. */ }
      setVideoState();
    };
    if (video && motion) {
      video.addEventListener('play', setVideoState, { signal });
      video.addEventListener('pause', setVideoState, { signal });
      video.addEventListener('error', () => { motion.classList.remove('is-playing'); }, { signal });
      reducedMotion.addEventListener('change', () => {
        if (reducedMotion.matches) { wantsPlayback = false; video.pause(); }
      }, { signal });
      document.addEventListener('visibilitychange', () => { if (document.hidden) video.pause(); else play(); }, { signal });
      if ('IntersectionObserver' in window) {
        mediaObserver = new IntersectionObserver(([entry]) => {
          inView = entry.isIntersecting;
          if (inView) play(); else video.pause();
        }, { threshold: 0.05 });
        mediaObserver.observe(motion);
      }
      const begin = () => {
        wantsPlayback = !reducedMotion.matches && !navigator.connection?.saveData;
        if (wantsPlayback) play();
      };
      if (document.readyState === 'complete') begin();
      else window.addEventListener('load', begin, { once: true, signal });
    }

    // Keep homepage-only space beneath the shared footer, restoring it on section unload.
    const spacer = document.createElement('div');
    spacer.setAttribute('aria-hidden', 'true');
    spacer.dataset.hh26Spacer = '';
    spacer.style.display = 'block';
    document.body.appendChild(spacer);
    const visiblyOpen = (element) => {
      if (element.closest('[hidden], [inert], details:not([open])')) return false;
      const style = window.getComputedStyle(element);
      if (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse' || style.opacity === '0') return false;
      const viewport = window.visualViewport;
      const left = viewport?.offsetLeft || 0;
      const top = viewport?.offsetTop || 0;
      const right = left + (viewport?.width || window.innerWidth);
      const bottom = top + (viewport?.height || window.innerHeight);
      return [...element.getClientRects()].some((rect) =>
        rect.width > 1 && rect.height > 1 &&
        Math.min(rect.right, right) - Math.max(rect.left, left) > 1 &&
        Math.min(rect.bottom, bottom) - Math.max(rect.top, top) > 1
      );
    };
    const refresh = () => {
      raf = 0;
      if (!sticky) return;
      const focus = document.activeElement;
      const inputFocused = focus && (focus.matches('input:not([type="checkbox"]):not([type="radio"]), textarea, select') || focus.isContentEditable);
      const viewport = window.visualViewport;
      const keyboardOpen = inputFocused && viewport && viewport.height < window.innerHeight * 0.8;
      const selectors = 'dialog[open], [role="dialog"][aria-modal="true"], menu-drawer details[open], header-drawer details[open], cart-drawer.active, cart-drawer[open], .drawer.active, .shopify-pc__banner__dialog';
      const overlayOpen = [...document.querySelectorAll(selectors)].some(visiblyOpen);
      const obscured = overlayOpen || keyboardOpen;
      sticky.classList.toggle('is-obscured', !!obscured);
      sticky.inert = !!obscured;
      const ariaHidden = String(!!obscured);
      if (sticky.getAttribute('aria-hidden') !== ariaHidden) sticky.setAttribute('aria-hidden', ariaHidden);
      spacer.style.height = mobile.matches ? `${sticky.getBoundingClientRect().height + 30}px` : '0px';
    };
    const schedule = () => { if (!raf) raf = requestAnimationFrame(refresh); };
    const observer = new MutationObserver(schedule);
    observer.observe(document.body, { subtree: true, attributes: true, attributeFilter: ['open', 'class', 'aria-modal', 'aria-hidden'] });
    window.addEventListener('resize', schedule, { passive: true, signal });
    window.visualViewport?.addEventListener('resize', schedule, { passive: true, signal });
    document.addEventListener('focusin', schedule, { signal });
    document.addEventListener('focusout', schedule, { signal });
    mobile.addEventListener('change', schedule, { signal });
    refresh();

    root.hh26Cleanup = () => {
      controller.abort();
      observer.disconnect();
      mediaObserver?.disconnect();
      cancelAnimationFrame(raf);
      video?.pause();
      spacer.remove();
      delete root.dataset.hh26Ready;
    };
  };
  document.querySelectorAll('[data-hush-homepage]').forEach(init);
  document.addEventListener('shopify:section:load', (event) => event.target.querySelectorAll('[data-hush-homepage]').forEach(init));
  document.addEventListener('shopify:section:unload', (event) => event.target.querySelectorAll('[data-hush-homepage]').forEach((root) => root.hh26Cleanup?.()));
})();
